import { MultiPurposeParagraph } from "@/components/Atoms/Paragraph";
import { Title } from "@/components/Atoms/Title";
import { ButtonMaster } from "@/components/Atoms/Buttons";
import styles from "./index.module.scss";
import { VariantProps, cva } from "class-variance-authority";
import { useRouter } from "next/router";
import { routerHandler } from "@/lib/routes/router-links-handler";
import {
  resolveButtonCustomIcon,
  resolveButtonIconColor,
  translateRecipeDifficulty,
} from "@/lib/functions";
import { useTranslation } from "next-i18next";

export type RecipeCardVariantProps = VariantProps<typeof RecipeCardVariants>;
const RecipeCardVariants = cva(`flex flex-row justify-between  `, {
  variants: {
    layout: {
      default: ` border  p-2 ${styles.border}`,
      gridRounded: `p-4 ${styles.borderRound}`,
      featured: "",
    },
  },
  defaultVariants: {
    layout: "default",
  },
});

export interface RecipeCardProps
  extends React.HTMLAttributes<HTMLHeadingElement>,
    RecipeCardVariantProps {
  post: any;
  cta: any;
  color: any;
  excerpt: any;
  labelColor?: any;
  showPrepHighlights?: boolean;
}

//BUTTON VARIANTS

export type ButtonVariantProps = VariantProps<typeof ButtonVariants>;
const ButtonVariants = cva(`  `, {
  variants: {
    layout: {
      default: ``,
      gridRounded: `mt-4 flex`,
      featured: "",
    },
    prepHighlights: {
      true: "",
      false: "",
    },
  },
  compoundVariants: [
    {
      layout: "gridRounded",
      prepHighlights: true,
      class: "justify-center",
    },
    {
      layout: "gridRounded",
      prepHighlights: false,
      class: "justify-start",
    },
  ],
  defaultVariants: {
    layout: "default",
    prepHighlights: true,
  },
});

export default function RecipeCard({
  post,
  color,
  excerpt,
  cta,
  layout,
  labelColor,
  showPrepHighlights,
}: RecipeCardProps) {
  const { locale } = useRouter();
  const { t } = useTranslation();
  const style = {
    "--color": excerpt?.color?.hex,
    "--labelColor": labelColor?.color?.hex,
  } as React.CSSProperties;
  const prepHighlightsVisible = showPrepHighlights !== false;
  return (
    <article className="col-span-2  p-4 px-6 gap-2 flex flex-col h-full justify-between md:min-h-[15rem]  ">
      <div>
        <Title
          className="titleRecipeCard"
          tag={"h3"}
          data={post.name}
          color={color?.color}
        />
        <MultiPurposeParagraph
          data={post.alternativeHeadline}
          color={excerpt?.color}
        />
      </div>
      <div className="gap-2 flex flex-col">
        {prepHighlightsVisible && (
          <div style={style} className={` ${RecipeCardVariants({ layout })}`}>
            {post.recipeDetails
              .filter((detail: any) => detail?.detailType == "prepTime")
              .map((time: any, i: number) => (
                <div className="flex-1 flex">
                  <MultiPurposeParagraph
                    key={i}
                    data={time.value}
                    color={excerpt?.color}
                  />
                </div>
              ))}{" "}
            <div
              style={style}
              className={`${styles.border} flex-1 justify-end flex border-l-2`}
            >
              <MultiPurposeParagraph
                data={translateRecipeDifficulty(post.difficulty, t)}
                color={excerpt?.color}
              />
            </div>
          </div>
        )}
        <div
          className={`${ButtonVariants({ layout, prepHighlights: prepHighlightsVisible })}`}
        >
          {cta && (
            <ButtonMaster
              type={cta.buttonStyle}
              cta={cta.typeColor?.color}
              color={cta.labelColor?.color}
              customIcon={resolveButtonCustomIcon(cta)}
              customColor={resolveButtonIconColor(cta)}
              link={routerHandler({
                typename: "RecipeRecord",
                slug: post.slug,
                locale,
              })}
            >
              {cta.buttonLabel?.label}
            </ButtonMaster>
          )}
        </div>
      </div>
    </article>
  );
}
